import type { IPaginatedResponse } from '@/apiClient'
import { adminQueryKeys } from '@/apiClient/queries/useAdminQueries'
import type { IGpt, IGptListItem } from '@/apiClient/types/gpt'
import { queryClient } from '@/lib/queryClient'

import type { InfiniteData } from '@tanstack/vue-query'

/** Sidebar pinned GPTs query — keep in sync with MenuItemGpts.vue */
export const PINNED_GPTS_QUERY_FILTERS = {
  is_pinned: true as const,
  size: 100,
}

const PINNED_GPTS_QUERY_KEY = ['gpts', PINNED_GPTS_QUERY_FILTERS] as const

const patchListItems = (
  items: IGptListItem[],
  gptId: string,
  pinned: boolean,
): IGptListItem[] | null => {
  let changed = false
  const next = items.map((item) => {
    if (item.id !== gptId) return item
    changed = true
    return { ...item, is_pinned: pinned }
  })
  return changed ? next : null
}

/** Find a GPT list row in any cached list/infinite/get query (for sidebar pin updates). */
export function findGptListItemInCaches(
  gptId: string,
): IGptListItem | undefined {
  const pinned = queryClient.getQueryData<IPaginatedResponse<IGptListItem>>(
    PINNED_GPTS_QUERY_KEY,
  )
  const fromPinned = pinned?.items.find((gpt) => gpt.id === gptId)
  if (fromPinned) return fromPinned

  for (const [, data] of queryClient.getQueriesData<
    IPaginatedResponse<IGptListItem>
  >({
    predicate: (query) => {
      const [root, second] = query.queryKey
      return root === 'gpts' && second !== 'infinite' && second !== 'get'
    },
  })) {
    const match = data?.items.find((gpt) => gpt.id === gptId)
    if (match) return match
  }

  for (const [, data] of queryClient.getQueriesData<
    InfiniteData<IPaginatedResponse<IGptListItem>>
  >({
    predicate: (query) => {
      const [root, second] = query.queryKey
      return root === 'gpts' && second === 'infinite'
    },
  })) {
    for (const page of data?.pages ?? []) {
      const match = page.items.find((gpt) => gpt.id === gptId)
      if (match) return match
    }
  }

  // Both edit pages cache a full GPT, under their own key each (see
  // `adminQueryKeys.gpt`), and either one is a usable list row.
  const fullGpt =
    queryClient.getQueryData<IGpt>(['gpts', 'get', gptId]) ??
    queryClient.getQueryData<IGpt>(adminQueryKeys.gpt(gptId))
  if (!fullGpt) return undefined

  const { config: _config, ...listItem } = fullGpt
  return listItem as IGptListItem
}

/** Add or remove a GPT from the sidebar pinned-list query cache. */
export function updatePinnedSidebarCache(
  gptId: string,
  pinned: boolean,
  gptItem?: IGptListItem,
) {
  queryClient.setQueryData<IPaginatedResponse<IGptListItem>>(
    PINNED_GPTS_QUERY_KEY,
    (old) => {
      if (!pinned) {
        if (!old?.items?.length) return old
        const items = old.items.filter((gpt) => gpt.id !== gptId)
        if (items.length === old.items.length) return old
        return {
          ...old,
          items,
          total: Math.max(0, (old.total ?? items.length) - 1),
        }
      }

      if (old?.items.some((gpt) => gpt.id === gptId)) {
        return old
      }

      if (!gptItem) return old

      const nextItem = { ...gptItem, is_pinned: true }
      if (!old) {
        return {
          items: [nextItem],
          page: 1,
          pages: 1,
          size: PINNED_GPTS_QUERY_FILTERS.size,
          total: 1,
        }
      }

      return {
        ...old,
        items: [...old.items, nextItem],
        total: (old.total ?? old.items.length) + 1,
      }
    },
  )
}

/** Update `is_pinned` in the cached single-GPT query read by detail views (topbar menu). */
function patchGptPinnedInDetailCache(gptId: string, pinned: boolean) {
  queryClient.setQueryData<IGpt>(['gpts', 'get', gptId], (old) =>
    old ? { ...old, is_pinned: pinned, pinned } : old,
  )
}

/** Update `is_pinned` in cached GPT list/infinite-list/detail queries without a full refetch. */
export function patchGptPinnedInCaches(gptId: string, pinned: boolean) {
  patchGptPinnedInDetailCache(gptId, pinned)

  queryClient.setQueriesData<IPaginatedResponse<IGptListItem>>(
    {
      predicate: (query) => {
        const [root, second] = query.queryKey
        return root === 'gpts' && second !== 'infinite' && second !== 'get'
      },
    },
    (old) => {
      if (!old?.items) return old
      const items = patchListItems(old.items, gptId, pinned)
      return items ? { ...old, items } : old
    },
  )

  queryClient.setQueriesData<InfiniteData<IPaginatedResponse<IGptListItem>>>(
    {
      predicate: (query) => {
        const [root, second] = query.queryKey
        return root === 'gpts' && second === 'infinite'
      },
    },
    (old) => {
      if (!old?.pages) return old

      let changed = false
      const pages = old.pages.map((page) => {
        const items = patchListItems(page.items, gptId, pinned)
        if (!items) return page
        changed = true
        return { ...page, items }
      })

      return changed ? { ...old, pages } : old
    },
  )
}

/**
 * Apply a pin/unpin result across Vue Query caches (incl. sidebar pinned list).
 * This is the shared "event" path — no separate event bus needed.
 */
export function applyGptPinStateChange(
  gptId: string,
  pinned: boolean,
  sourceGpt?: IGptListItem,
) {
  const gptItem = sourceGpt ?? findGptListItemInCaches(gptId)
  patchGptPinnedInCaches(gptId, pinned)
  updatePinnedSidebarCache(gptId, pinned, gptItem)

  if (pinned && !gptItem) {
    void queryClient.refetchQueries({ queryKey: PINNED_GPTS_QUERY_KEY })
  }
}
